import Axios, { AxiosResponse } from 'axios';
import React from 'react';
import { style } from 'typestyle';

import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { telemetryConsumerWrapper } from '~commonInfra/telemetryEvents/contexts/TelemetryConsumerWrapper';
import { ContentClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { ContentClickedEventHandler } from '~commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '~commonInfra/telemetryEvents/util/EventHandlerFactories';

import { Messages } from '../../../codegen/Localize';
import { ContentDraftStatus } from '../../../drafts/serverApi/types/ContentDraft';
import { DraftStyles } from '../../../drafts/styles/DraftStyles';
import { CardViewStyles } from './CardViewStyles';

export const ThumbnailTestId = 'thumbnail';

export type ThumbnailProps = Pick<React.AriaAttributes, 'aria-label'> & {
  /** The url to navigate to when clicking the thumbnail; the thumbnail will have a link element only if this is present */
  readonly navigateUrl?: string;
  /** The default image url, used when `thumbnailImageUrl` is not present or fails to load */
  readonly defaultImageUrl: string;
  /** Whether the link dragging should be disabled or not */
  readonly allowDragAndDrop: boolean;
  /** Whether the `navigateUrl` should be opened in a new tab or current tab */
  readonly openLinkInNewTab?: boolean;
  /** The image url */
  readonly thumbnailImageUrl?: string;
  /** Whether or not to set `background-size: cover` when styling the thumbnail */
  readonly imageCoverBackground?: boolean;
  /** Whether the thumbnail should have a draft status indicator or not */
  readonly draftStatus?: string;
  /** Whether the thumbnail should look like a stack of cards (useful for workbook thumbnail) or just one card */
  readonly decorate?: boolean;
  /** Whether or not to highlight the thumbnail */
  readonly hoveredOrSelected?: boolean;
  /** Whether the `thumbnailImageUrl` should be treated as a base64 encoded svg data URI instead of an actual image url */
  readonly useBase64EncodedImage?: boolean;
  /** Height override for the thumbnail; if not present then the height defaults to 184px specified in `CardViewStyles` */
  readonly customHeight?: number;
  /** Whether or not to show focus indicator */
  readonly allowFocusIndicator?: boolean;
  /** Text to be rendered at the center of thumbnail */
  readonly centeredText?: string;
}

export interface ThumbnailState {
  readonly loadThumbnailImage: boolean;
}

const CARD_STACK_DECORATION = <div className={CardViewStyles.ThumbnailWorkbookDecoration} />;

const focusIndicatorStyle: string = style({
  $nest: {
    '&:focus': CommonStyles.Focus.Indicator
  }
});

export class Thumbnail extends React.Component<ThumbnailProps, ThumbnailState> {
  private thumbnailImage: HTMLImageElement;
  private encodedSvg: string;

  constructor(props: ThumbnailProps) {
    super(props);
    this.state = { loadThumbnailImage: false };
    this.thumbnailImage = new Image();
  }

  public componentDidMount() {
    if (this.props.thumbnailImageUrl) {
      // If we are using a data uri, we need to fetch the content first
      if (this.props.useBase64EncodedImage) {
        Axios.get(this.props.thumbnailImageUrl)
          .then((result: AxiosResponse) => {
            this.encodedSvg = result.data;
            this.setState({loadThumbnailImage: true});
          });
      } else {
        this.thumbnailImage.src = this.props.thumbnailImageUrl;
        this.thumbnailImage.onload = () => {
          this.setState({ loadThumbnailImage: true });
        };
      }
    }
  }

  public componentWillUnmount() {
    this.thumbnailImage.onload = null;
  }

  private buildThumbnailOverlayDiv(): JSX.Element {
    return (
      <div data-tb-test-id={'thumbnail-overlay'}
        className={CardViewStyles.SelectOverlay}
        style={this.props.customHeight ? { height: this.props.customHeight + 'px' } : null}>
        {
          (this.props.draftStatus === ContentDraftStatus.unpublished) &&
          <div className={DraftStyles.DraftThumbnailTextStyle}>
            {Messages.draftThumbnailHoverTextForUnpublished()}
          </div>
        }
      </div>
    );
  }

  public render() {
    if (this.props.navigateUrl && !this.props['aria-label']) {
      throw new Error(`aria-label must be provided in order to meet accessibility requirements`);
    }

    const classNames = this.state.loadThumbnailImage ? [CardViewStyles.ThumbnailPreloaded] : [CardViewStyles.Thumbnail];
    const imageUri = this.props.useBase64EncodedImage ? `data:image/svg+xml;base64,${this.encodedSvg}` : this.props.thumbnailImageUrl;
    const backgroundImageUrl = this.state.loadThumbnailImage ? imageUri : this.props.defaultImageUrl;

    if (this.props.hoveredOrSelected) {
      classNames.push(CardViewStyles.CardHighlight);
    }
    if (this.props.allowFocusIndicator) {
      classNames.push(focusIndicatorStyle);
    }

    const loadedImageOrCoverBackground = this.props.imageCoverBackground || this.state.loadThumbnailImage;

    const thumbnailStyle = {
      backgroundImage: `url(${backgroundImageUrl})`,
      backgroundSize: loadedImageOrCoverBackground ? 'cover' : '',
      height: this.props.customHeight ? this.props.customHeight + 'px' : null
    };

    return telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) => {
      return this.props.navigateUrl ?
        <div data-tb-test-id={ThumbnailTestId}>
          <a href={this.props.navigateUrl}
            target={this.props.openLinkInNewTab ? '_blank' : null}
            rel="noopener noreferrer"
            /**
             * If card reordering allowed, disable link dragging.
             * Otherwise, set `draggable` explicitly to undefined rather than true.
             * (When `draggable` is true, the browser-provided drag avatar looks too similar
             * to the reorder card feature's avatar, which may confuse users.)
             */
            draggable={this.props.allowDragAndDrop ? false : undefined}
            onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
            className={classNames.join(' ')}
            style={thumbnailStyle}
            aria-label={this.props['aria-label']}
          >
            {this.props.centeredText && <div className={CardViewStyles.ThumbnailCenteredText}>{this.props.centeredText}</div>}
            {this.buildThumbnailOverlayDiv()}
          </a>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div> :
        <div data-tb-test-id={ThumbnailTestId}>
          <div
            className={classNames.join(' ')}
            style={thumbnailStyle}
          >
            {this.props.centeredText && <div className={CardViewStyles.ThumbnailCenteredText}>{this.props.centeredText}</div>}
            {this.buildThumbnailOverlayDiv()}
          </div>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div>;
    });
  }
}
